import type { ReactNode } from 'react'
import { RadioGroup, RadioGroupItem } from '~/components/ui/radio-group'
import { useFieldContext } from '~/hooks/form-hooks'
import { cn } from '~/lib/utils'
import { fieldErrorMessage } from './field'

export interface RadioCardOption<T extends string> {
  value: T
  title: ReactNode
  description: ReactNode
}

interface RadioCardsProps<T extends string> {
  /** Libellé au-dessus des cartes ; sans libellé, `aria-label` nomme le groupe. */
  label?: string
  'aria-label'?: string
  name?: string
  options: readonly RadioCardOption<T>[]
  value: T
  onChange: (next: T) => void
  className?: string
}

/**
 * Choix exclusif en cartes titre + description (composant Figma « RadioOption » 12842:28012, palette
 * « Back-Office 2026 ») : l'option choisie prend le fond vert clair. Contrôlé ; `RadioCardsField` le
 * branche sur TanStack Form.
 */
export function RadioCards<T extends string>({
  label,
  'aria-label': ariaLabel,
  name,
  options,
  value,
  onChange,
  className,
}: RadioCardsProps<T>) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <p className="text-xs leading-[14px] font-semibold text-sage-500">{label}</p>}
      <RadioGroup
        aria-label={label ?? ariaLabel}
        name={name}
        value={value}
        onValueChange={(next) => onChange(next as T)}
        className="gap-2"
      >
        {options.map((option) => (
          // oxlint-disable-next-line jsx-a11y/label-has-associated-control -- le contrôle (radio) est imbriqué dans le label.
          <label
            key={option.value}
            className={cn(
              'flex min-h-[52px] cursor-pointer items-start gap-2 rounded-lg p-2',
              option.value === value && 'bg-primary-50',
            )}
          >
            <RadioGroupItem
              value={option.value}
              className="size-[18px] border-[1.5px] border-background-200 bg-sidebar shadow-none data-checked:border-primary-600 data-checked:bg-sidebar [&_[data-slot=radio-group-indicator]]:size-full [&_[data-slot=radio-group-indicator]]:text-primary-600 [&_svg]:size-2"
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-sm leading-4 font-semibold text-sage-700">{option.title}</span>
              <span className="text-xs leading-[14px] font-medium text-sage-500">
                {option.description}
              </span>
            </span>
          </label>
        ))}
      </RadioGroup>
    </div>
  )
}

/** `RadioCards` branché sur le champ TanStack Form courant, avec son premier message d'erreur. */
export function RadioCardsField<T extends string>({
  label,
  options,
  className,
}: {
  label: string
  options: readonly RadioCardOption<T>[]
  className?: string
}) {
  const field = useFieldContext<T>()
  const error = fieldErrorMessage(field.state.meta.errors)

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <RadioCards
        label={label}
        name={field.name}
        options={options}
        value={field.state.value}
        onChange={field.handleChange}
      />
      {error && <em className="text-xs text-destructive opacity-70">{error}</em>}
    </div>
  )
}
